import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Area,
  AreaChart,
  Legend,
  type TooltipValueType,
} from "recharts";
import type { T_ChartData } from "@lattice/frontend/types/charts";
import { isMultiSeries, getSeriesNames } from "@lattice/frontend/types/charts";
import {
  AXIS_COLOR,
  CHART_COLORS,
  GRID_COLOR,
  TOOLTIP_STYLE,
  TOOLTIP_LABEL_STYLE,
  TOOLTIP_ITEM_STYLE,
  formatLargeNumber,
} from "./chart-theme";

export const LineChartComponent = ({
  data,
  area = false,
}: {
  data: T_ChartData;
  area?: boolean;
}) => {
  const multiSeries = isMultiSeries(data);
  const seriesNames = getSeriesNames(data);

  const commonProps = {
    data: data.data,
    margin: { top: 5, right: 30, left: 20, bottom: 5 },
  };

  const commonChildren = (
    <>
      <CartesianGrid
        strokeDasharray="3 3"
        stroke={GRID_COLOR}
        vertical={false}
      />
      <XAxis
        dataKey="label"
        stroke={AXIS_COLOR}
        fontSize={12}
        tickLine={false}
        axisLine={false}
      />
      <YAxis
        stroke={AXIS_COLOR}
        fontSize={12}
        tickLine={false}
        axisLine={false}
        tickFormatter={formatLargeNumber}
        domain={["dataMin", "dataMax"]}
        padding={{ top: 10, bottom: 10 }}
      />
      <Tooltip
        contentStyle={TOOLTIP_STYLE}
        labelStyle={TOOLTIP_LABEL_STYLE}
        itemStyle={!multiSeries ? TOOLTIP_ITEM_STYLE : undefined}
        wrapperStyle={{ zIndex: 1000 }}
        allowEscapeViewBox={{ x: true, y: true }}
        formatter={(value?: TooltipValueType) =>
          typeof value === "number" ? formatLargeNumber(value) : undefined
        }
      />
      {multiSeries && (
        <Legend wrapperStyle={{ fontSize: 12 }} iconType="line" />
      )}
    </>
  );

  return (
    <div className="h-72 w-full min-w-0">
      <ResponsiveContainer
        width="100%"
        height={288}
        minWidth={240}
        minHeight={288}
      >
        {area ? (
          <AreaChart {...commonProps}>
            {commonChildren}
            {seriesNames.map((name, i) => (
              <Area
                key={name}
                type="monotone"
                dataKey={name}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
                fillOpacity={0.2}
                strokeWidth={2}
                name={name}
                connectNulls={false}
              />
            ))}
          </AreaChart>
        ) : (
          <LineChart {...commonProps}>
            {commonChildren}
            {seriesNames.map((name, i) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                strokeWidth={2}
                dot={!multiSeries}
                name={name}
                connectNulls={false}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};
